import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to the Core back panel image */
const CORE_BACK_PANEL_PATH = '/assets/images/installation-guide/core-pro-back.png';

/**
 * ConnectCoreStep - Slide 2.7: Connect Ethernet to Core
 *
 * Shows instructions for plugging the network cable into the Core:
 * - Close-up image of Core back panel with LAN-1 port highlighted
 * - Instructions to plug cable into LAN-1 port
 * - Chatbot help button for cable length issues
 */
export const ConnectCoreStep: React.FC = () => {
  const { goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_cableToCore');
  }, [setMainStepId]);

  const handleConnected = () => {
    goToStep('connect-power');
  };

  const guidanceButtons = [
    { label: __("My network cable isn't long enough."), flowId: 'cable-too-short' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Connect ethernet to Core.')}
      </Typography>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        {__('Now, plug the other end of the same network cable into your Core.')}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        {__(
          'Plug the other end of the cable into the port labeled LAN-1 on your Core. This connects your Core to the Internet through your router.'
        )}
      </Typography>

      {/* Core back panel image */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
        }}
      >
        <Box
          component="img"
          src={CORE_BACK_PANEL_PATH}
          alt={__('Core back panel showing LAN-1 port')}
          sx={{
            maxWidth: '100%',
            maxHeight: 280,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Guidance section with chatbot button */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__('My router and Core are connected')}
        onPrimary={handleConnected}
      />
    </Box>
  );
};
